<template>
  <div class="form-input-wrapper" :class="[{ 'has-tooltip': field.tooltip }]">
    <div
      class="form-input"
      :class="[
        {
          readonly: field.readonly,
          isEditing: mode === 'edit' || mode === 'add',
          disabled: isFieldDisabled(field),
        },
      ]"
      :name="`field_${field.name}`"
    >
      <label class="sc-field-label" :for="field.name" v-if="!field.hideLabel">{{
        field.label
      }}
        <span
          v-if="field.tooltip"
          class="sc-tooltip icon-circle-question-mark"
          :title="field?.tooltipText"
        ></span>
      </label>

      <div v-if="field.readonly" class="sc-field-value-container">
        <div class="sc-field-value">
          <span>{{ field.value }}</span>          
        </div>
      </div>

      <Field
        v-else-if="field.type === 'select'"
        as="select"
        :label="field.label"
        :name="field.name"
        v-model="value"
        :disabled="isFieldDisabled(field)"
        :title="getFieldDisplayValue(formData[field.name])"
        @change="handleFieldUpdate(formData[field.name], $event)"
        @blur="emit('blur', $event)"
      >
        <option
          v-for="option in field.options"
          :key="option.id"
          :value="option.id"
        >
          {{ option?.label || option.name }}
        </option>
      </Field>
      <CurrencyInput
        v-else-if="field.type === 'money'"
        v-model="value"
        :name="field.name"
        :disabled="isFieldDisabled(field)"
        :field="field"
        @change="handleFieldUpdate(formData[field.name])"
        @blur="emit('blur', $event)"
      />
      <Field
        v-else
        :label="field.label"
        :id="field.name"
        :name="field.name"
        :type="field.type"
        :disabled="isFieldDisabled(field)"
        v-model="value"
        @change="handleFieldUpdate(formData[field.name], $event)"
        @blur="emit('blur', $event)"
      >
      </Field>

      <div v-if="mode !== 'view'" class="error-container">
        <ErrorMessage :name="field.name" v-slot="{ message }">
          <span v-if="message" class="error">
            {{ message }}
          </span>
        </ErrorMessage>
      </div>
    </div>    
  </div>
</template>

<script setup lang="ts">
import {
  reactive,
  computed,
  watchEffect,
  defineEmits,
  watch,
  ref,
  onMounted,
} from "vue";
import { Schema } from "yup";
import { Field, ErrorMessage } from "vee-validate";
import CurrencyInput from "./CurrencyInput.vue";
import { FormField, FormOption } from "@/types/form";
import { useField } from "vee-validate";

const props = defineProps<{
  field: FormField;
  schema?: Schema;
  isSubmitting: boolean;
  isEditing: boolean;
  mode: "add" | "edit" | "view";
  modelValue?: null | string | number | Date | object;
  initialValue?: any;
}>();

const emit = defineEmits<{
  (
    e: "update:model-value",
    event: undefined | null | string | number | Date | object | unknown
  ): void;
  (e: "blur", event: FocusEvent | Event): void;
}>();

const formData = reactive<Record<string, any>>({});

const { errors, value, errorMessage, handleChange, validate } = useField(
  props.field.name,
  props.schema
);

const isFormValid = computed(() => {
  return (errors: Partial<Record<string, string | undefined>>) =>
    errors && Object.keys(errors).length === 0;
});

function getFieldDisplayValue(value: number) {
  if (props.field.type === "select") {
    const option = props.field?.options?.find(
      (f: FormOption) => f.id === value
    );

    return option?.label || option?.name;
  }
  return null;
}

function isFieldDisabled(field: FormField) {
  const disabledOnEdit = props.mode === "edit" && field.disabledOnEdit === true;
	const disabledOnAdd = props.mode === "add" && field.disabledOnAdd === true;

  return props.mode === "view" || props.isSubmitting || disabledOnEdit || disabledOnAdd;
}

function handleFieldUpdate(
  input: number | string | null | undefined,
  event?: Event | null
) {
  emit("update:model-value", input);
}

watchEffect(() => {
  formData[props.field.name] = props.field.value;
});

watch(value, (newValue, oldValue) => {
  emit("update:model-value", newValue);
  handleChange(value.value);
});

watch(
  () => props.modelValue,
  (newValue) => {
    formData[props.field.name] = newValue;
    value.value = newValue;
  }
);
</script>
<style scoped>
.form-input:not(.isEditing) input,
.form-input:not(.isEditing) select {
  border: transparent;
  background: transparent;
  pointer-events: none;
  cursor: default;
}

.form-input.isEditing input:disabled {
  background: #e9e9e9;
}
.form-input {
  position: relative;
}
.error {
  color: #ff0000;
  font-size: 12px;
}
.error-container {
  min-height: 22px;
  line-height: normal;
}
.sc-field-label {
  color: #000;
  font-family: "Montserrat";
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  margin-bottom: 5px;
  line-height: 16px;
}

.sc-field-value {
  color: #000;
  font-family: "Montserrat";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 114.286% */
  line-height: normal;
  display: flex;
  align-items: center;
  height: 33px;
}

.sc-tooltip.icon-circle-question-mark {
  margin: 8px;
  cursor: pointer;
}

/* Style the inputs */
input,
input[type="text"],
input[type="date"],
input[type="text"]#currency {
  box-sizing: border-box;
  margin: 0;
  border: 1px solid #ced1d9;
  background-color: #fff;
  font-family: "Montserrat";
  color: #000;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px !important;
  padding: 0;
  min-width: 50%;
  height: 33px;
}

/* Style the dropdown */
select {
  width: fit-content;
  width: 100%;
  border: 1px solid #ced1d9;
  color: #000;
  font-family: "Montserrat";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 114.286% */
  padding: 0;
  height: 33px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  background-color: #fff;
  transition: 0.4s;
}

.form-input[name="field_disbursementStatusTypeId"] {
  max-width: 225px;
}

.form-input[name="field_disbursementDate"] {
  max-width: 145px;
}

.form-input-wrapper:is(.has-tooltip) {
  display: flex;
  align-items: center;
}

@media screen and (max-width: 1300px) {
  select {
    height: 33px;
  }
}
@media screen and (max-width: 1200px) {
}

@media screen and (max-width: 620px) {
  .form-input-wrapper:is(.has-tooltip) {
    display: flex;
    align-items: center;
  }
}
</style>
